<script setup>
import { computed, ref } from 'vue';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { standardDateTime } from 'shared/helpers/timeHelper';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const emit = defineEmits(['merged']);

const { t } = useI18n();
const store = useStore();

const dialogRef = ref(null);
const campaigns = ref([]);
const targetId = ref(null);

const uiFlags = useMapGetter('campaigns/getUIFlags');
const isUpdating = computed(() => uiFlags.value.isUpdating);

const sourceIds = computed(() =>
  campaigns.value
    .map(campaign => campaign.id)
    .filter(id => id !== targetId.value)
);

// The survivor keeps the earliest date anyway (Campaigns::MergeService#settle_target), so the
// campaign that started the series is the one that makes sense to keep by default.
const open = selectedCampaigns => {
  campaigns.value = [...selectedCampaigns].sort(
    (a, b) => a.scheduled_at - b.scheduled_at
  );
  targetId.value = campaigns.value[0]?.id ?? null;
  dialogRef.value.open();
};

const handleDialogConfirm = async () => {
  const count = sourceIds.value.length;
  try {
    await store.dispatch('campaigns/merge', {
      id: targetId.value,
      sourceIds: sourceIds.value,
    });
    useAlert(t('CAMPAIGN.MERGE.API.SUCCESS_MESSAGE', { count }, count));
    emit('merged');
    dialogRef.value.close();
  } catch (error) {
    useAlert(t('CAMPAIGN.MERGE.API.ERROR_MESSAGE'));
  }
};

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    type="edit"
    width="2xl"
    overflow-y-auto
    :title="t('CAMPAIGN.MERGE.TITLE')"
    :description="t('CAMPAIGN.MERGE.DESCRIPTION')"
    :confirm-button-label="t('CAMPAIGN.MERGE.CONFIRM')"
    :cancel-button-label="t('CAMPAIGN.MERGE.CANCEL')"
    :is-loading="isUpdating"
    :disable-confirm-button="!sourceIds.length"
    @confirm="handleDialogConfirm"
  >
    <p class="mb-1 text-sm font-medium text-n-slate-12">
      {{ t('CAMPAIGN.MERGE.KEEP_LABEL') }}
    </p>
    <div class="flex flex-col gap-1 overflow-y-auto max-h-96">
      <label
        v-for="campaign in campaigns"
        :key="campaign.id"
        class="flex items-center gap-3 p-2 rounded-lg cursor-pointer hover:bg-n-alpha-1"
      >
        <input
          v-model="targetId"
          type="radio"
          name="merge-target"
          :value="campaign.id"
          class="grid w-4 h-4 border-2 rounded-full shadow cursor-pointer appearance-none place-items-center border-n-strong checked:bg-n-brand checked:border-n-brand"
        />
        <span class="flex-1 min-w-0">
          <span class="block text-sm truncate text-n-slate-12">
            {{ campaign.title }}
          </span>
          <span class="block text-xs text-n-slate-11 tabular-nums">
            {{
              t('CAMPAIGN.MERGE.CANDIDATE_DETAILS', {
                date: standardDateTime(campaign.scheduled_at),
                count: campaign.total_recipients_count,
              })
            }}
          </span>
        </span>
      </label>
    </div>
  </Dialog>
</template>
